import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { dataExchangeApi } from "@/services/data-exchange-api.service";
import type { ImportMode } from "@/types/api-v2";
import { RefreshCw, Download, FileSpreadsheet, Loader2 } from "lucide-react";

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  running: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-amber-100 text-amber-800",
};

export default function DataExportPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<ImportMode>("objects");
  const [since, setSince] = useState("");
  const [partnerCode, setPartnerCode] = useState("");
  const [jobId, setJobId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  async function createJob() {
    setCreating(true);
    try {
      const job = await dataExchangeApi.createExportJob({
        mode,
        since_timestamp: since || null,
        target_partner_code: partnerCode || null,
        object_kls_codes: [],
        organization_codes: [],
        region: null,
        object_uuids: [],
        include_media: true,
      });
      toast({
        title: "Задача экспорта создана",
        description: `Задача №${job.id}: ${job.status}`,
      });
      queryClient.invalidateQueries({ queryKey: ["export-jobs"] });
      setJobId(job.id);
    } catch (e: any) {
      toast({
        title: "Ошибка экспорта",
        description: e?.message || "Не удалось создать задачу экспорта",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  }

  const { data: job } = useQuery({
    queryKey: ["export-job", jobId],
    queryFn: () => (jobId ? dataExchangeApi.getExportJob(jobId) : Promise.resolve(null)),
    enabled: jobId !== null,
    refetchInterval: (query) =>
      query.state.data?.status === "running" || query.state.data?.status === "pending" ? 2000 : false,
  });

  async function downloadTemplate() {
    try {
      const blob = await dataExchangeApi.downloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "gpm-import-template.zip";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({
        title: "Ошибка загрузки шаблона",
        description: e?.message || "Не удалось скачать шаблон",
        variant: "destructive",
      });
    }
  }

  async function downloadJob() {
    if (!job) return;
    try {
      const blob = await dataExchangeApi.downloadExportPackage(job.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = job.file_name ?? `gpm-export-${job.id}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({
        title: "Ошибка скачивания",
        description: e?.message || "Пакет ещё не готов или не найден",
        variant: "destructive",
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-md font-medium">Экспорт данных</h3>
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Скачать шаблон с примером
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1">
            <Label>Режим</Label>
            <Select value={mode} onValueChange={(v) => setMode(v as ImportMode)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Режим" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="objects">Объекты + геометрии + media</SelectItem>
                <SelectItem value="sync">Sync-пакет (delta, для партнёра)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Изменения после (delta, опц.)</Label>
            <Input
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Код партнёра (для sync)</Label>
            <Input
              value={partnerCode}
              onChange={(e) => setPartnerCode(e.target.value)}
              placeholder="Напр. STD"
              disabled={mode !== "sync"}
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Button onClick={createJob} disabled={creating}>
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Создать задачу экспорта
          </Button>
          {job && (
            <div className="flex items-center gap-3 text-sm">
              <Badge className={STATUS_BADGE[job.status] ?? ""}>{job.status}</Badge>
              {job.size_bytes ? <span>{(job.size_bytes / 1024).toFixed(1)} КБ</span> : null}
              {job.status === "completed" && (
                <Button variant="outline" size="sm" onClick={downloadJob}>
                  <Download className="mr-2 h-4 w-4" />
                  Скачать пакет
                </Button>
              )}
              {job.error_message && (
                <span className="text-red-700 max-w-sm truncate" title={job.error_message}>
                  {job.error_message}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-md font-medium">Текущая задача (#{jobId ?? "—"})</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ["export-job", jobId] })}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>Задача</TableCell>
              <TableCell>Режим</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Файл</TableCell>
              <TableCell className="text-right">Действия</TableCell>
            </TableRow>
            {job ? (
              <TableRow key={job.id}>
                <TableCell>{job.id}</TableCell>
                <TableCell>{job.mode}</TableCell>
                <TableCell>
                  <Badge className={STATUS_BADGE[job.status] ?? ""}>{job.status}</Badge>
                </TableCell>
                <TableCell>{job.file_name ?? "—"}</TableCell>
                <TableCell className="text-right">
                  {job.status === "completed" && (
                    <Button variant="ghost" size="sm" onClick={downloadJob}>
                      <Download className="mr-1 h-4 w-4" />
                      Скачать
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                  Задачи ещё нет — создайте её выше
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}